import { useState, useRef, useEffect, type FormEvent } from 'react';
import type React from 'react';
import { Bot, Globe, GraduationCap, MessageSquare, PanelLeft, PanelRight, Paperclip, Camera, FolderPlus, Folder, ChevronRight, Plus, ArrowUp, ArrowLeft, ArrowDown, Users, X } from 'lucide-react';
import { SkeletonMessage } from './Skeleton';
import MessageBubble from './MessageBubble';
import TypingDots from './TypingDots';
import type { Message, TabType, Professional } from '../App';
import chiefyMascot from '../assets/images/ChiefyMascot.png';
import { isChatQuotaExhausted } from '../userService';
import './ChatRoom.css';

type ChatPartner = {
    name: string;
    avatar: string;
    online: boolean;
};

type GroupChatInfo = {
    name: string;
    memberCount: number;
    members: Professional[];
};

type ChatRoomProps = {
    messages: Message[];
    placeholder: string;
    title: string;
    subtitle?: string;
    isLoadingMessages?: boolean;
    activeTab: TabType;
    onMenuClick: () => void;
    showMenu: boolean;
    onTogglePanel?: () => void;
    isPanelOpen?: boolean;
    onBack?: () => void;
    chatPartner?: ChatPartner;
    groupChat?: GroupChatInfo;
    chatId?: string;
    projectId?: string;
    projects?: { id: string; name: string }[];
    onMoveToProject?: (projectId: string | null) => void;
    onCreateProject?: () => void;
    onSendMessage?: (content: string, file?: File | null) => Promise<void>;
    userData?: import('../userService').BackendUserWithPlan | null;
};

function ChatRoom({
    messages,
    placeholder,
    title,
    subtitle,
    isLoadingMessages = false,
    activeTab,
    onMenuClick,
    showMenu,
    onTogglePanel,
    isPanelOpen,
    onBack,
    chatPartner,
    groupChat,
    chatId,
    projectId,
    projects = [],
    onMoveToProject,
    onCreateProject,
    onSendMessage,
    userData,
}: ChatRoomProps) {
    const [input, setInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [messagesListEl, setMessagesListEl] = useState<HTMLDivElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesAreaRef = useRef<HTMLDivElement | null>(null);
    const [panelTooltip, setPanelTooltip] = useState<{ top: number; left: number } | null>(null);
    const [attachedFile, setAttachedFile] = useState<File | null>(null);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [showAttachMenu, setShowAttachMenu] = useState(false);
    const [showProjectSubmenu, setShowProjectSubmenu] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachMenuRef = useRef<HTMLDivElement>(null);

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleScreenshot = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
            const track = stream.getVideoTracks()[0];
            const canvas = document.createElement('canvas');
            const video = document.createElement('video');
            video.srcObject = stream;
            await video.play();
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d')?.drawImage(video, 0, 0);
            track.stop();
            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
                    setAttachedFile(file);
                }
            }, 'image/png');
        } catch {
            // User cancelled or browser doesn't support
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            setAttachedFile(file);
        }
    };

    const removeFile = () => {
        setAttachedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const showPanelTooltip = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setPanelTooltip({ top: rect.top + rect.height / 2, left: rect.left - 12 });
    };
    const hidePanelTooltip = () => setPanelTooltip(null);
    const handlePanelToggle = () => {
        hidePanelTooltip();
        onTogglePanel?.();
    };

    const hasStreamingAssistantMessage = messages.some(
        (m) => m.role === 'assistant' && m.streaming,
    );

    // When the user submits, there can be a short delay before the backend
    // emits the first streamed assistant message. Show a placeholder bubble
    // during that gap so the UI feels responsive.
    const showThinkingPlaceholder =
        isSubmitting && Boolean(onSendMessage) && !hasStreamingAssistantMessage;

    const quotaExhausted = isChatQuotaExhausted(userData);
    const isInputDisabled = isSubmitting || isLoadingMessages || quotaExhausted;

    // Check if user is near the bottom of the scrollable area
    const isNearBottom = (threshold = 100): boolean => {
        const scrollContainer = messagesAreaRef.current;
        if (!scrollContainer) return true; // Default to true if we can't check
        
        const distanceFromBottom = scrollContainer.scrollHeight - (scrollContainer.scrollTop + scrollContainer.clientHeight);
        return distanceFromBottom <= threshold;
    };

    const scrollToBottom = (behavior: ScrollBehavior = 'auto', force = false) => {
        if (!force && !isNearBottom(300)) {
            return;
        }
        const doScroll = () => {
            const el = messagesAreaRef.current;
            if (el) {
                el.scrollTo({ top: el.scrollHeight, behavior });
            }
        };
        requestAnimationFrame(doScroll);
        // Double-fire to catch late DOM updates (streaming)
        setTimeout(doScroll, 50);
    };

    // Keep chat pinned to bottom whenever content changes.
    // Force during streaming so text always stays visible.
    useEffect(() => {
        const hasStreaming = messages.some(m => m.streaming);
        scrollToBottom(
            messages.length > 0 ? 'smooth' : 'auto',
            hasStreaming || showThinkingPlaceholder
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, showThinkingPlaceholder]);

    // When switching chats/projects or finishing load, jump to bottom (force).
    useEffect(() => {
        scrollToBottom('auto', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, isLoadingMessages]);

    // When the right panel is toggled, layout width changes; keep pinned to bottom (force).
    useEffect(() => {
        scrollToBottom('auto', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPanelOpen]);

    // Close attach menu on click outside
    useEffect(() => {
        if (!showAttachMenu) return;
        const handler = (e: MouseEvent) => {
            if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
                setShowAttachMenu(false);
                setShowProjectSubmenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showAttachMenu]);

    // Show/hide scroll-to-bottom button based on scroll position
    useEffect(() => {
        const el = messagesAreaRef.current;
        if (!el) return;
        const onScroll = () => setShowScrollBtn(!isNearBottom(200));
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagesAreaRef.current]);

    // When the chat area resizes (e.g., right panel opens/closes with a transition),
    // keep pinned to bottom only if user is near bottom. Window resize won't fire for this.
    useEffect(() => {
        if (!messagesListEl) return;

        const resizeObserver = new ResizeObserver(() => {
            // Only auto-scroll on resize if user is near bottom
            scrollToBottom('auto', false);
        });

        resizeObserver.observe(messagesListEl);
        return () => resizeObserver.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagesListEl]);

    // Also keep pinned on window resize, but only if near bottom.
    useEffect(() => {
        const handleResize = () => scrollToBottom('auto', false);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        const hasContent = input.trim() || attachedFile;
        if (!hasContent || isInputDisabled) return;

        const messageContent = input.trim();
        const fileToUpload = attachedFile;

        setInput('');
        setAttachedFile(null);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setIsSubmitting(true);
        scrollToBottom('smooth', true);

        try {
            if (onSendMessage) {
                await onSendMessage(messageContent, fileToUpload);
            }
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get tab-specific icon
    const getTabIcon = () => {
        switch (activeTab) {
            case 'assistant': return <Bot size={18} />;
            case 'networking': return <Globe size={18} />;
            case 'education': return <GraduationCap size={18} />;
            default: return <MessageSquare size={18} />;
        }
    };

    // Check if this is a DM chat (has chatPartner) or group chat
    const isDmChat = !!chatPartner;
    const isGroupChat = !!groupChat;

    return (
        <div className={`chatroom ${messages.length === 0 && !isLoadingMessages && !showThinkingPlaceholder ? 'chatroom--empty' : ''}`}>
            {/* Header */}
            <header className={`chatroom-header ${isDmChat ? 'dm-header' : ''} ${isGroupChat ? 'group-header' : ''}`} onClick={() => messagesAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}>
                {/* Back button for DM/Group chats */}
                {onBack && (
                    <button type="button" className="back-btn" onClick={onBack} aria-label="Go back">
                        <ArrowLeft size={18} />
                    </button>
                )}
                {/* Sidebar toggle — visible on mobile only */}
                {!onBack && showMenu && (
                    <button type="button" className="menu-btn" onClick={onMenuClick} aria-label="Toggle sidebar">
                        <PanelLeft size={18} />
                    </button>
                )}

                {isGroupChat && groupChat ? (
                    <div className="header-content group-content">
                        <div className="group-avatar-header">
                            <Users size={16} />
                        </div>
                        <div className="group-info">
                            <h1 className="header-title">{title}</h1>
                            {subtitle && <span className="header-subtitle">{subtitle}</span>}
                        </div>
                    </div>
                ) : isDmChat && chatPartner ? (
                    <div className="header-content dm-content">
                        <div className={`dm-avatar ${chatPartner.online ? 'online' : ''}`}>
                            {chatPartner.avatar}
                            {chatPartner.online && <span className="status-indicator" />}
                        </div>
                        <div className="dm-info">
                            <h1 className="header-title">{title}</h1>
                            {subtitle && <span className="header-subtitle">{subtitle}</span>}
                        </div>
                    </div>
                ) : (
                    <div className="header-content">
                        <span className="header-icon">{getTabIcon()}</span>
                        <h1 className="header-title">{title}</h1>
                        {projectId && (
                            <span className="header-project-label">
                                <Folder size={12} />
                                {projects.find(p => p.id === projectId)?.name || 'Project'}
                            </span>
                        )}
                    </div>
                )}

                <div className="header-actions">
                    {/* Toggle Panel button */}
                    {onTogglePanel && (
                        <button
                            type="button"
                            className={`action-btn panel-toggle ${isPanelOpen ? 'active' : ''}`}
                            onClick={handlePanelToggle}
                            onMouseEnter={showPanelTooltip}
                            onMouseLeave={hidePanelTooltip}
                        >
                            <PanelRight size={18} />
                        </button>
                    )}
                </div>
            </header>

            {/* Messages Area */}
            <div className="messages-area" ref={messagesAreaRef} aria-live="polite" aria-relevant="additions">
                {isLoadingMessages ? (
                    <div className="messages-list" ref={setMessagesListEl}>
                        <SkeletonMessage align="right" />
                        <SkeletonMessage align="left" />
                        <SkeletonMessage align="right" />
                        <SkeletonMessage align="left" />
                        <div ref={messagesEndRef} />
                    </div>
                ) : messages.length === 0 && !showThinkingPlaceholder ? (
                    <div className="empty-state">
                        {activeTab === 'assistant' && (
                            <img src={chiefyMascot} alt="Chiefy Mascot" className="chiefy-mascot" />
                        )}
                        {activeTab === 'assistant' ? (
                            <>
                                <h2>Welcome — I'm Chiefy</h2>
                                <p>Ask me anything to get started.</p>
                            </>
                        ) : (
                            <>
                                <div className="empty-icon">{getTabIcon()}</div>
                                <h2>Start a conversation</h2>
                                <p>Type a message below to get started</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="messages-list" ref={setMessagesListEl}>
                        {messages.map((message) => (
                            <MessageBubble key={message.id} message={message} />
                        ))}
                        {showThinkingPlaceholder && (
                            <div key="thinking" className="message assistant">
                                <div className="bubble assistant">
                                    <TypingDots />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Scroll to bottom button */}
            {showScrollBtn && messages.length > 0 && (
                <button
                    type="button"
                    className="scroll-to-bottom-btn"
                    onClick={() => scrollToBottom('smooth', true)}
                    aria-label="Scroll to bottom"
                >
                    <ArrowDown size={18} />
                </button>
            )}

            {/* Welcome content — shown above input when empty */}
            {messages.length === 0 && !isLoadingMessages && !showThinkingPlaceholder && activeTab === 'assistant' && (
                <div className="empty-welcome">
                    <img src={chiefyMascot} alt="Chiefy" className="chiefy-mascot" />
                    <h2 className="welcome-title">Welcome — I'm Chiefy</h2>
                    <p className="welcome-subtitle">Ask me anything to get started.</p>
                </div>
            )}

            {/* Chat quota bar — visible only for limited plans */}
            {/* Input Area */}
            <form className="input-area" onSubmit={handleSubmit}>
                <div className="input-container">
                    {/* Chips: project badge + file attachment */}
                    {(projectId && !messages.length || attachedFile) && (
                        <div className="input-chips">
                            {projectId && !messages.length && (
                                <div className="input-chip input-chip--project">
                                    <Folder size={14} />
                                    <span>{projects.find(p => p.id === projectId)?.name || 'Project'}</span>
                                    <button type="button" className="input-chip-remove" onClick={() => onMoveToProject?.(null)} title="Remove from project">
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                            {attachedFile && (
                                <div className="input-chip input-chip--file">
                                    <Paperclip size={14} />
                                    <span>{attachedFile.name}</span>
                                    <button type="button" className="input-chip-remove" onClick={removeFile} title="Remove attachment">
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        aria-hidden="true"
                    />
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (input.trim() && !isInputDisabled) {
                                    (e.target as HTMLTextAreaElement).form?.requestSubmit();
                                }
                            }
                        }}
                        placeholder={placeholder}
                        disabled={isInputDisabled}
                        className="chat-input"
                        rows={1}
                    />
                    <div className="input-bottom-row">
                        <div className="input-actions-left">
                            <div className="attach-menu-container" ref={attachMenuRef}>
                                <button
                                    type="button"
                                    className={`input-action attach-toggle ${showAttachMenu ? 'active' : ''}`}
                                    title="Add content"
                                    onClick={() => setShowAttachMenu(!showAttachMenu)}
                                >
                                    <Plus size={18} />
                                </button>
                                {showAttachMenu && (
                                    <div className="attach-menu">
                                        {!showProjectSubmenu ? (
                                            <>
                                                <button type="button" className="attach-menu-item" onClick={() => { handleFileClick(); setShowAttachMenu(false); }}>
                                                    <Paperclip size={18} />
                                                    <span>Add files or photos</span>
                                                </button>
                                                {typeof navigator?.mediaDevices?.getDisplayMedia === 'function' && (
                                                    <button type="button" className="attach-menu-item" onClick={() => { handleScreenshot(); setShowAttachMenu(false); }}>
                                                        <Camera size={18} />
                                                        <span>Take a screenshot</span>
                                                    </button>
                                                )}
                                                <div className="attach-menu-divider" />
                                                <button type="button" className="attach-menu-item" onClick={() => setShowProjectSubmenu(true)}>
                                                    <FolderPlus size={18} />
                                                    <span>{projectId ? 'Move to project' : 'Add to project'}</span>
                                                    <ChevronRight size={14} className="attach-menu-chevron" />
                                                </button>
                                            </>
                                        ) : (
                                            <div className="attach-submenu">
                                                <button type="button" className="attach-menu-item attach-menu-back" onClick={() => setShowProjectSubmenu(false)}>
                                                    <ArrowLeft size={16} />
                                                    <span>Back</span>
                                                </button>
                                                <div className="attach-menu-divider" />
                                                {projectId && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="attach-menu-item"
                                                            onClick={() => { onMoveToProject?.(null); setShowAttachMenu(false); setShowProjectSubmenu(false); }}
                                                        >
                                                            <X size={16} />
                                                            <span>Remove from project</span>
                                                        </button>
                                                        <div className="attach-menu-divider" />
                                                    </>
                                                )}
                                                {projects.filter(p => p.id !== projectId).map(p => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        className="attach-menu-item"
                                                        onClick={() => { onMoveToProject?.(p.id); setShowAttachMenu(false); setShowProjectSubmenu(false); }}
                                                    >
                                                        <Folder size={16} />
                                                        <span>{p.name}</span>
                                                    </button>
                                                ))}
                                                <div className="attach-menu-divider" />
                                                <button type="button" className="attach-menu-item attach-menu-create" onClick={() => { onCreateProject?.(); setShowAttachMenu(false); setShowProjectSubmenu(false); }}>
                                                    <Plus size={16} />
                                                    <span>Start new project</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="send-btn"
                            disabled={!input.trim() || isInputDisabled}
                            title="Send message"
                        >
                            <ArrowUp size={16} />
                        </button>
                    </div>
                </div>
            </form>

            {/* Footer — Claude-style: centered disclosure below input */}
            {activeTab === 'assistant' && (
                <div className="chat-footer">
                    <p className="ai-disclosure">
                        Chiefy is AI and can make mistakes. Please double-check responses.
                    </p>
                </div>
            )}

            {/* Panel toggle tooltip */}
            {panelTooltip && (
                <div
                    className="panel-toggle-tooltip"
                    style={{ top: panelTooltip.top, left: panelTooltip.left }}
                >
                    {isPanelOpen ? 'Close panel' : 'Open panel'}
                </div>
            )}
        </div>
    );
}

export default ChatRoom;
